import { ArrowLeftIcon } from "@heroicons/react/24/solid";
import { Formik, Form, FormikHelpers } from "formik";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Yup from "yup";
import { FormInput } from "@/core/components/form-input";
import { LoadingSpinner } from "@/core/components/loading-spinner";
import { Button } from "@/core/components/ui/button";
import { Card, CardContent } from "@/core/components/ui/card";
import { Stack } from "@/core/components/ui/stack/stack";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import {
  PASSWORD_RESET_SOURCE_PARAM,
  PasswordResetError,
  PASSWORD_RESET_ERROR_CODE,
  WELCOME_EMAIL_PASSWORD_RESET_SOURCE,
  useAuth,
} from "@/core/auth";
import { AutomaticLoginFailure } from "./automatic-login-failure";
import { useWelcomeLinkValidation } from "./use-welcome-link-validation";

const resetPasswordSchema = Yup.object().shape({
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
  confirm_password: Yup.string()
    .equals([Yup.ref("password")], "Passwords must match")
    .required("Password is required"),
});

interface ResetPasswordFormValues {
  password: string;
  confirm_password: string;
}

export default function ResetPassword() {
  const {
    resetPassword,
    forgotPasswordEmail: authEmail,
    forgotPasswordVerificationCode: authVerificationCode,
  } = useAuth();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  // Get email and code from either URL parameters or auth context
  const email = searchParams.get("email") || authEmail || "";
  const verificationCode =
    searchParams.get("code") || authVerificationCode || "";
  const isWelcomeEmailFlow =
    searchParams.get(PASSWORD_RESET_SOURCE_PARAM) ===
    WELCOME_EMAIL_PASSWORD_RESET_SOURCE;

  const [showPassword, setShowPassword] = useState(false);
  const [automaticLoginFailed, setAutomaticLoginFailed] = useState(false);
  const [hasStartedPasswordSubmission, setHasStartedPasswordSubmission] =
    useState(false);
  const isValidatingWelcomeLink = useWelcomeLinkValidation({
    email,
    verificationCode,
    enabled: isWelcomeEmailFlow && !hasStartedPasswordSubmission,
  });

  const handleSubmit = async (
    values: ResetPasswordFormValues,
    { setSubmitting }: FormikHelpers<ResetPasswordFormValues>,
  ) => {
    setHasStartedPasswordSubmission(true);

    try {
      if (!email || !verificationCode) {
        throw new Error("Missing email or verification code");
      }

      await resetPassword({
        password: values.password,
        email,
        verification_code: verificationCode,
      });
      toast.success(
        isWelcomeEmailFlow
          ? "Your password has been set successfully"
          : "Your password has been reset successfully",
      );
    } catch (error) {
      if (error instanceof PasswordResetError) {
        switch (error.code) {
          case PASSWORD_RESET_ERROR_CODE.invalidCode:
            toast.error(
              "This reset link is invalid or has expired — please request a new one",
            );
            navigate("/signup");
            break;
          case PASSWORD_RESET_ERROR_CODE.loginFailed:
            setAutomaticLoginFailed(true);
            toast.error(error.message);
            break;
          default:
            // Surface the backend validation detail when available.
            toast.error(error.message);
        }
      } else {
        toast.error(
          error instanceof Error && error.message
            ? error.message
            : "Something went wrong, please try again",
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (isValidatingWelcomeLink) {
    return (
      <Stack
        fullWidth
        justify="center"
        align="center"
        className="min-h-[calc(100vh-67px)]"
      >
        <LoadingSpinner />
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      align="center"
      justify="center"
      fullWidth
      className="min-h-[calc(100vh-67px)] overflow-hidden p-6"
    >
      <Card className="w-full max-w-md px-4 py-6">
        {automaticLoginFailed ? (
          <AutomaticLoginFailure />
        ) : (
          <Formik
            initialValues={{ password: "", confirm_password: "" }}
            validationSchema={resetPasswordSchema}
            onSubmit={handleSubmit}
          >
            {({ getFieldProps, isSubmitting, isValid }) => (
              <Form>
                <CardContent>
                  <Stack gap="lg" fullWidth>
                    <Typography variant="subtitleW1" className="mt-4">
                      {isWelcomeEmailFlow
                        ? "Set Your Password"
                        : "Reset Password"}
                    </Typography>

                    {isWelcomeEmailFlow && email ? (
                      <Typography variant="bodyW1" textColor="secondary">
                        Please set your password for <b>{email}</b> to finish
                        setting up your account.
                      </Typography>
                    ) : null}

                    {isWelcomeEmailFlow && !email ? (
                      <Typography variant="bodyW1" textColor="secondary">
                        Please set your password to finish setting up your
                        account.
                      </Typography>
                    ) : null}

                    {!isWelcomeEmailFlow && email ? (
                      <Typography variant="bodyW1" textColor="secondary">
                        Please enter your new password for <b>{email}</b> and
                        confirm it.
                      </Typography>
                    ) : null}

                    {!isWelcomeEmailFlow && !email ? (
                      <Typography variant="bodyW1" textColor="secondary">
                        Please enter your new password and confirm it.
                      </Typography>
                    ) : null}

                    <FormInput
                      id="password"
                      type={showPassword ? "text" : "password"}
                      label="Password"
                      placeholder="Enter your password"
                      {...getFieldProps("password")}
                      fullWidth
                      RightActionComponent={
                        <Typography
                          variant="subtitleW3"
                          textColor="interactive"
                          className="mt-5 cursor-pointer self-end"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? "Hide" : "Show"}
                        </Typography>
                      }
                    />

                    <FormInput
                      id="confirm_password"
                      type={showPassword ? "text" : "password"}
                      label="Confirm Password"
                      placeholder="Confirm your password"
                      {...getFieldProps("confirm_password")}
                      fullWidth
                      RightActionComponent={
                        <Typography
                          variant="subtitleW3"
                          textColor="interactive"
                          className="mt-5 cursor-pointer self-end"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? "Hide" : "Show"}
                        </Typography>
                      }
                    />

                    <Stack fullWidth gap="sm">
                      <Button
                        type="submit"
                        size="lg"
                        fullWidth
                        disabled={!isValid}
                        loading={isSubmitting}
                      >
                        Continue
                      </Button>

                      <Button
                        type="button"
                        size="lg"
                        variant="text"
                        fullWidth
                        className="[&_svg]:size-5"
                        leftIcon={<ArrowLeftIcon />}
                        onClick={() => navigate("/login")}
                      >
                        Back to Login
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Form>
            )}
          </Formik>
        )}
      </Card>
    </Stack>
  );
}
